import { useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useLinkedEmailViewerStore } from '@/stores/linkedEmailViewerStore';
import { useEmailActionsStore } from '@/stores/emailActionsStore';
import { useUIStore } from '@/stores/uiStore';
import { EmailDetail } from '@/features/email-detail/EmailDetail';
import type { ReplyContext } from '@/types/ui';

/**
 * Standalone viewer for an email opened from OUTSIDE the mail workspace — a
 * todo's linked email, an order journey's source document, a briefing ref.
 * Driven entirely by linkedEmailViewerStore; mount ONCE per workspace that
 * opens emails this way (Todo). Reply switches to the mail workspace
 * with the compose sheet pre-seeded.
 */
export function LinkedEmailViewerDialog() {
  const email = useLinkedEmailViewerStore((state) => state.email);
  const open = useLinkedEmailViewerStore((state) => state.open);
  const closeViewer = useLinkedEmailViewerStore((state) => state.closeViewer);
  const archiveEmail = useEmailActionsStore((state) => state.archiveEmail);
  const deleteEmail = useEmailActionsStore((state) => state.deleteEmail);
  const setReplyContext = useUIStore((state) => state.setReplyContext);
  const setComposeOpen = useUIStore((state) => state.setComposeOpen);
  const setActiveWorkspace = useUIStore((state) => state.setActiveWorkspace);

  const handleClose = useCallback(() => {
    closeViewer();
  }, [closeViewer]);

  const handleArchive = useCallback(
    (emailId: string) => {
      if (!email?.folderId) {
        return;
      }
      void archiveEmail(emailId, email.accountId, email.folderId);
    },
    [archiveEmail, email]
  );

  const handleDelete = useCallback(
    (emailId: string) => {
      if (!email?.folderId) {
        return;
      }
      void deleteEmail(emailId, email.accountId, email.folderId);
    },
    [deleteEmail, email]
  );

  const handleReply = useCallback(
    (context: ReplyContext) => {
      setReplyContext(context);
      setActiveWorkspace('mail');
      setComposeOpen(true);
      closeViewer();
    },
    [closeViewer, setActiveWorkspace, setComposeOpen, setReplyContext]
  );

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? handleClose() : null)}>
      {/* h (not max-h): only a DEFINITE container height makes the flexed item's
          size definite, which EmailDetail's h-full → ScrollArea chain needs to
          resolve — under max-h the percentages fall back to auto and the body
          just clips. Verified headless: viewport 5159px, canScroll false. */}
      <DialogContent className="flex h-[90vh] max-w-5xl w-[95vw] flex-col overflow-hidden">
        {email ? (
          <div className="min-h-0 flex-1">
            <EmailDetail
              email={email}
              loading={false}
              onBack={handleClose}
              onDelete={(targetId) => handleDelete(targetId)}
              onArchive={(targetId) => handleArchive(targetId)}
              onReply={handleReply}
              accountId={email.accountId}
              mode="standalone"
            />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
